import { useEffect } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import { useUpdateUserMutation } from 'store/apis/user.api';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUser } from 'store/slices/auth.slice';
import { toast } from 'react-toastify';

interface UsePasswordFormManagerProps {
  next?: (data?: any) => void;
}

export const usePasswordFormManager = ({ next = () => {} }: UsePasswordFormManagerProps) => {
  const currentUser = useAppSelector(selectCurrentUser);
  const [updateUser, { isLoading, isSuccess, isError, ...rest }] = useUpdateUserMutation();

  const initialValues = {
    old_password: '',
    new_password: '',
    new_password_confirm: '',
  };

  const validationSchema = Yup.object({
    old_password: Yup.string().required('Old password is required'),
    new_password: Yup.string()
      .required('New password is required')
      .min(6, 'New password must be at least 6 characters'),
    new_password_confirm: Yup.string()
      .oneOf([Yup.ref('new_password'), null], 'Please make sure your passwords match')
      .required('Confirm new password is required'),
  });

  const formManager = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const { old_password, new_password } = values;

      updateUser({
        user: currentUser,
        old_password,
        new_password,
      });
    },
  });

  useEffect(() => {
    if (isError) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      toast.error(rest.error?.data.payload.message);
    }

    if (!isLoading && isSuccess && !isError) {
      const { data } = rest;

      next(data);

      toast.success('The user password has been successfully updated.');
    }
  }, [isLoading, isSuccess, isError]);

  return [
    formManager,
    {
      isLoading,
      isSuccess,
      isError,
    },
  ] as [
    FormikProps<typeof initialValues>,
    {
      isLoading: boolean;
      isSuccess: boolean;
      isError: boolean;
    },
  ];
};
